import React from 'react';
import { Wallet, Calendar, Archive, Download, Clock, CheckCircle, AlertCircle, ReceiptText, Landmark } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { PERIODE_OPTIONS } from './nosDepensesUtils';

// En-tete calque sur celui de MaCaisse : bandeau bleu #246990, titre + icone,
// actions a droite, puis rangee de pastilles de statut.
const CentralHeader = ({
  currentUser,
  filterPeriode, setFilterPeriode,
  onNouveauMouvement, onCloturerMois, onExporter,
  clotureDisabled,
  caissesEnAttente, caissesAvecObservation, caissesValidees
}) => {
  return (
    <>
      <Card className="border-0 shadow-sm overflow-visible relative z-10">
        <CardHeader className="bg-[#246990] text-white rounded-t-lg overflow-visible">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-3 text-2xl">
                <Landmark size={32} />
                Caisse centrale
              </CardTitle>
              <p className="text-white text-sm mt-1 font-semibold">
                Contrôleur : {currentUser?.nom} {currentUser?.prenom}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg p-2">
                <Calendar size={18} className="text-white" />
                <Select value={filterPeriode} onValueChange={setFilterPeriode}>
                  <SelectTrigger className="h-8 w-36 bg-white text-[#246990] border-0 font-medium">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PERIODE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                size="sm"
                className="bg-white text-[#246990] hover:bg-white/90 transition-colors"
                onClick={onNouveauMouvement}
              >
                <Wallet size={16} className="mr-2" />
                Nouveau mouvement
              </Button>

              <Button
                size="sm"
                className="bg-[#c66d59] hover:bg-[#ae7467]/90 text-white transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                onClick={onCloturerMois}
                disabled={clotureDisabled}
              >
                <Archive size={16} className="mr-2" />
                Clôturer le mois
              </Button>

              <Button
                size="sm"
                className="bg-white/20 text-white hover:bg-white/30 transition-colors"
                onClick={onExporter}
              >
                <Download size={16} className="mr-2" />
                Exporter
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-2">
            {caissesEnAttente > 0 && (
              <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-yellow-500/20 text-white border border-white/30">
                <Clock size={14} className="mr-1" />
                {caissesEnAttente} caisse{caissesEnAttente > 1 ? 's' : ''} à valider
              </span>
            )}

            {caissesAvecObservation > 0 && (
              <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-orange-500/20 text-white border border-white/30">
                <AlertCircle size={14} className="mr-1" />
                {caissesAvecObservation} avec observation{caissesAvecObservation > 1 ? 's' : ''}
              </span>
            )}

            {caissesValidees > 0 && (
              <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-emerald-500/20 text-white border border-white/30">
                <CheckCircle size={14} className="mr-1" />
                {caissesValidees} validée{caissesValidees > 1 ? 's' : ''}
              </span>
            )}

            {caissesEnAttente === 0 && caissesAvecObservation === 0 && (
              <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-emerald-500/20 text-white border border-white/30">
                <CheckCircle size={14} className="mr-1" />
                Aucun contrôle en attente
              </span>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Note d'information -- meme traitement que MaCaisse */}
      <div className="mt-6 p-4 bg-[#f9f0ee] rounded-lg border border-[#ae7467]/20 flex items-center">
        <div className="p-2 bg-[#ae7467] rounded-full mr-4">
          <ReceiptText className="text-white" size={20} />
        </div>
        <div className="flex-1">
          <h5 className="font-medium text-[#ae7467]">information importante</h5>
          <p className="text-sm text-gray-700 leading-relaxed">
            La caisse centrale reçoit les <strong>remises journalières</strong> des gestionnaires après clôture de leur portefeuille.
            Une remise validée crédite définitivement le <strong>solde réel central</strong> et ne peut plus être modifiée sans procédure exceptionnelle.
            <br /><br />
            Les <strong>dotations</strong> sortent de ce solde pour alimenter les caisses des gestionnaires :
            une sortie est refusée si le solde central validé est insuffisant.
            <br />
            <span className="font-semibold text-[#ae7467]">Une caisse ne peut être validée tant qu'une observation reste non résolue.</span>
          </p>
        </div>
      </div>
    </>
  );
};

export default CentralHeader;
